import { Box, Typography, Chip, alpha, Stack } from '@mui/material';
import { Place, Phone, Person, LocalShipping } from '@mui/icons-material';
import { DispatchRow, SERVICE_META, timeAgo, formatStatus } from './dispatchTypes';
import { DISPATCH } from './dispatchTheme';
import { formatMoney } from '../../utils/currency';

interface DispatchJobCardProps {
  row: DispatchRow;
  serialNo?: number;
  selected?: boolean;
  onClick: () => void;
}

export default function DispatchJobCard({ row, serialNo, selected, onClick }: DispatchJobCardProps) {
  const meta = SERVICE_META[row.service];
  const unassigned = row.partner === 'Unassigned' || row.partner === '-';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        mb: 0.75,
        borderRadius: `${DISPATCH.radiusSm}px`,
        cursor: 'pointer',
        bgcolor: selected ? DISPATCH.bgPanel : alpha(DISPATCH.bgPanel, 0.92),
        border: '1px solid',
        borderColor: selected ? meta.color : DISPATCH.border,
        boxShadow: selected ? DISPATCH.shadowSelected : DISPATCH.shadow,
        overflow: 'hidden',
        transition: 'all 0.15s ease',
        boxSizing: 'border-box',
        '&:hover': {
          bgcolor: 'background.paper',
          borderColor: alpha(meta.color, 0.45),
          boxShadow: `0 4px 14px ${alpha(meta.color, 0.1)}`,
        },
      }}
    >
      <Box sx={{ width: 4, flexShrink: 0, bgcolor: meta.color }} />

      <Box sx={{ display: 'flex', flex: 1, gap: 0.75, p: 1.25, minWidth: 0, overflow: 'hidden' }}>
        {serialNo != null && (
          <Typography
            variant="caption"
            fontWeight={800}
            color="text.secondary"
            sx={{ width: 18, flexShrink: 0, pt: 0.25 }}
          >
            {serialNo}
          </Typography>
        )}

        <Box flex={1} minWidth={0} overflow="hidden">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5} mb={0.5} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0} flex={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={800} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                #{row.ref}
              </Typography>
              <Chip
                label={meta.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: alpha(meta.color, 0.1),
                  color: meta.color,
                  maxWidth: '100%',
                }}
              />
            </Stack>
            <Typography variant="body2" fontWeight={800} color="primary.main" sx={{ flexShrink: 0 }}>
              {row.total > 0 ? `${formatMoney(row.total)}` : '—'}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25} minWidth={0}>
            <Person sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
            >
              {row.customer}
            </Typography>
          </Stack>
          {row.customerPhone && (
            <Stack direction="row" alignItems="center" spacing={0.35} mb={0.5} pl={2.25}>
              <Phone sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.customerPhone}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap mb={0.35}>
            <Chip
              label={formatStatus(row.status)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                bgcolor: alpha(meta.color, 0.08),
                color: meta.color,
              }}
            />
            {unassigned && (
              <Chip label="Unassigned" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.disabled" display="block" mb={0.5}>{timeAgo(row.createdAt)}</Typography>

          {row.pickupAddress && (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} mb={0.35} minWidth={0}>
              <Place sx={{ fontSize: 13, color: '#22c55e', mt: 0.1, flexShrink: 0 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1.35,
                  flex: 1,
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {row.pickupAddress}
              </Typography>
            </Stack>
          )}

          {row.dropoffAddress && (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} mb={0.35} minWidth={0}>
              <Place sx={{ fontSize: 13, color: '#ef4444', mt: 0.1, flexShrink: 0 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1.35,
                  flex: 1,
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {row.dropoffAddress}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
            <LocalShipping sx={{ fontSize: 13, color: unassigned ? 'error.main' : 'text.disabled', flexShrink: 0 }} />
            <Typography
              variant="caption"
              color={unassigned ? 'error.main' : 'text.secondary'}
              fontWeight={unassigned ? 700 : 500}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
            >
              {row.partner}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
